/* Cart remove → UNDO toast (spec 04/17).
   Captures the removed variant + qty before Dawn's removal flow runs,
   then offers a 5s UNDO that re-adds the line. */
(() => {
  if (window.__kashiraCartUndo) return;
  window.__kashiraCartUndo = true;

  /* ---------------------------------------------------------------------
     In-place cart re-render (Cart AJAX API + Section Rendering), the same
     contract Dawn's cart.js uses: ask /cart/*.js for the two cart sections and
     swap their `.js-contents` innerHTML. Replaces the old full-page reload so
     an inline blouse swap keeps scroll position and can reuse the 150ms qty
     crossfade from kashira-cart-motion.js.
     --------------------------------------------------------------------- */
  const CART_SECTIONS = ['main-cart-items', 'main-cart-footer'];

  const sectionIds = () =>
    CART_SECTIONS.map((id) => document.getElementById(id)?.dataset.id).filter(Boolean);

  /* The empty-state contract (`is-empty`, the conditional section padding)
     lives on the section ROOTS, outside `.js-contents` — so a contents swap
     alone can leave a repopulated cart hidden behind the empty-state CSS.
     Mirror the fresh root's state onto the live one. */
  const syncRootState = (live, fresh) => {
    if (!live || !fresh) return;
    live.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
    const sectionClasses = (el) => [...el.classList].filter((c) => c.startsWith('section-'));
    sectionClasses(live).forEach((c) => {
      if (!fresh.classList.contains(c)) live.classList.remove(c);
    });
    sectionClasses(fresh).forEach((c) => live.classList.add(c));
  };

  const renderSections = (sections) => {
    if (!sections) return false;
    let rendered = false;
    CART_SECTIONS.forEach((domId) => {
      const host = document.getElementById(domId);
      const html = host && sections[host.dataset.id];
      if (!host || !html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = doc.querySelector(`#${domId} .js-contents`);
      const target = host.querySelector('.js-contents');
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
      rendered = true;

      if (domId === 'main-cart-items') {
        syncRootState(host.closest('cart-items'), doc.querySelector('cart-items'));
      } else {
        syncRootState(host, doc.querySelector(`#${domId}`));
      }

      // The rail's delivery estimate lives outside `.js-contents` (so a typed
      // pincode survives a re-render), but its dispatch basis is cart-derived —
      // sync just that attribute so the estimate can never go stale.
      const freshDispatch = doc.querySelector('.kashira-summary-estimate kashira-pincode')?.dataset
        .dispatchDays;
      const estimate = host.querySelector('.kashira-summary-estimate kashira-pincode');
      if (estimate && freshDispatch) estimate.dataset.dispatchDays = freshDispatch;
    });
    return rendered;
  };

  // The header count is a section of its own; Dawn refreshes it on every cart
  // mutation, so an undo re-add must too or the badge understates the cart.
  const renderBubble = (sections) => {
    const html = sections && sections['cart-icon-bubble'];
    const host = document.getElementById('cart-icon-bubble');
    if (!html || !host) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (fresh) host.innerHTML = fresh.innerHTML;
  };

  // Fall back to a reload only if the section payload is missing/unusable, so
  // the cart is never left showing stale totals.
  const applyCart = (payload) => {
    renderBubble(payload?.sections);
    if (!renderSections(payload?.sections)) window.location.reload();
  };

  /* Reuse the drawer/cart 150ms qty crossfade for the swapped line. The motion
     asset diffs rows by line key, so a variant swap reads as a brand-new line
     and would play the 300ms enter instead — override it on this one row. */
  const crossfadeSwappedRow = (variantId) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Let kashira-cart-motion.js finish its own rAF pass first.
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => {
        const input = document.querySelector(`input[data-quantity-variant-id="${variantId}"]`);
        const row = input?.closest('.cart-item');
        if (!row) return;
        row.classList.remove('kashira-line-enter');
        row
          .querySelectorAll('.cart-item__price-wrapper .price, .cart-item__final-price, .cart-item__old-price')
          .forEach((el) => {
            el.classList.remove('kashira-price-swap');
            void el.offsetWidth;
            el.classList.add('kashira-price-swap');
            el.addEventListener('animationend', () => el.classList.remove('kashira-price-swap'), { once: true });
          });
      })
    );
  };

  const announce = (message) => {
    const region = document.getElementById('cart-live-region-text');
    if (!region || !message) return;
    // Dawn re-stamps aria-hidden=true on this region a second after every
    // mutation; writing while hidden is never announced.
    region.setAttribute('aria-hidden', 'false');
    region.textContent = message;
  };

  const postCart = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) {
        // 422 means the mutation was definitively rejected (e.g. sold out) —
        // callers use this to fail cleanly instead of reloading blind.
        const error = new Error(`${url} failed`);
        error.status = response.status;
        throw error;
      }
      return response.json();
    });

  // MOVE TO WISHLIST (cart page rows): save handle, then trigger removal.
  // The synthetic click below runs the remove listener synchronously, which
  // consumes `pendingMove` — the toast then reads "moved" instead of
  // "removed", and its UNDO knows to take the item back out of the wishlist.
  let pendingMove = null;

  document.addEventListener('click', (e) => {
    const moveBtn = e.target.closest('.kashira-move-wishlist');
    if (!moveBtn) return;
    const handle = moveBtn.dataset.handle;
    const store = window.KashiraWishlistStore;
    const added = Boolean(handle && store && !store.has(handle));
    if (added) store.toggle(handle);
    // Lines the platform forbids removing hide their remove control — the
    // move action must not proxy around that.
    const removeWrapper = document.getElementById(moveBtn.dataset.removeId);
    const removeLink =
      removeWrapper && !removeWrapper.classList.contains('hidden') ? removeWrapper.querySelector('a') : null;
    if (removeLink) {
      // e.detail is 0 for keyboard activation; the synthetic click below is
      // also 0, so the flag must be captured here, not in the remove listener.
      pendingMove = { handle, added, keyboard: e.detail === 0 };
      removeLink.click();
      pendingMove = null;
    } else {
      window.KashiraToast?.show(window.kashiraCartStrings?.movedToWishlist);
    }
  });

  /* Inline variant edit (cart page rows).

     The Cart AJAX API has no atomic swap — /cart/update.js cannot introduce a
     variant that is not already in the cart — so the swap is two mutations:
     add the new variant, then zero the old line by key. If the second call
     fails after the first succeeded the cart would hold BOTH lines and the
     shopper would pay twice, so the failure path runs a compensating rollback.
     /cart/add.js MERGES into an existing line of the same variant, so the
     rollback must restore that line's previous quantity — zeroing it would
     destroy a quantity the shopper already had. If the rollback itself fails
     we can no longer describe the cart truthfully from here, so we reload
     rather than leave a stale or wrong cart on screen. */
  const rollbackAdd = (addedKey, restoreQuantity) =>
    postCart(`${window.routes?.cart_change_url || '/cart/change'}.js`, {
      id: addedKey,
      quantity: restoreQuantity,
      sections: sectionIds(),
      sections_url: window.location.pathname,
    });

  document.addEventListener('change', async (e) => {
    const select = e.target.closest('.kashira-line-variant__select');
    if (!select) return;
    const newId = Number(select.value);
    if (!newId || newId === Number(select.dataset.current)) return;
    const oldKey = select.dataset.key;
    const row = select.closest('.cart-item');
    select.disabled = true;
    row?.classList.add('kashira-line-swapping');

    // Set once the add succeeded, so the catch knows a rollback is owed.
    let addedKey = null;
    let preLine = null;

    let addAttempted = false;
    try {
      // If the target variant is already a line, the add below merges into it
      // — remember its quantity so a rollback can restore it exactly.
      const cartBefore = await fetch(`${window.routes?.cart_url || '/cart'}.js`).then((r) => r.json());
      preLine =
        cartBefore.items.find(
          (item) =>
            item.id === newId &&
            (!item.properties || !Object.keys(item.properties).length) &&
            !item.selling_plan_allocation
        ) || null;

      addAttempted = true;
      const added = await postCart(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        id: newId,
        quantity: Number(select.dataset.quantity) || 1,
      });
      addedKey = added?.key || null;

      // The removal of the old line is the last mutation, so its response
      // carries the sections that describe the finished cart.
      const state = await postCart(`${window.routes?.cart_change_url || '/cart/change'}.js`, {
        id: oldKey,
        quantity: 0,
        sections: sectionIds(),
        sections_url: window.location.pathname,
      });
      applyCart(state);
      crossfadeSwappedRow(newId);
      announce(window.kashiraCartStrings?.lineUpdated);
    } catch (err) {
      if (addedKey) {
        // The add landed but the removal did not — undo the add so the cart is
        // exactly what it was before the shopper touched the select.
        try {
          const restoreQuantity = preLine && addedKey === preLine.key ? preLine.quantity : 0;
          applyCart(await rollbackAdd(addedKey, restoreQuantity));
        } catch (rollbackError) {
          // Cart state is now unknown to this page; refetch the truth.
          window.location.reload();
          return;
        }
      } else if (addAttempted && err?.status !== 422) {
        // Defensive: the add may have landed even though we never saw a line
        // key (unexpected/blank payload). We cannot target the new line for a
        // rollback, and guessing could delete the wrong row — so reload rather
        // than leave the shopper on a page that might understate their cart.
        // A 422 is different: the add was definitively rejected (sold out,
        // quantity cap), the cart is unchanged, and the toast below is the
        // honest response.
        window.location.reload();
        return;
      }
      select.disabled = false;
      select.value = select.dataset.current;
      row?.classList.remove('kashira-line-swapping');
      const message = window.kashiraCartStrings?.swapFailed;
      window.KashiraToast?.show(message);
      announce(message);
    }
  });

  // Mobile sticky checkout bar (cart page): visible while the real CTA is off-screen
  const stickyBar = document.querySelector('.kashira-cart-stickybar');
  if (stickyBar && 'IntersectionObserver' in window) {
    const ctas = document.querySelector('.cart__footer .cart__ctas');
    if (ctas) {
      new IntersectionObserver(([entry]) => {
        stickyBar.hidden = entry.isIntersecting;
      }).observe(ctas);
    } else {
      stickyBar.hidden = false;
    }
    const totalsSource = document.querySelector('#main-cart-footer .js-contents');
    if (totalsSource) {
      new MutationObserver(() => {
        const value = document.querySelector('#main-cart-footer .totals__total-value');
        const target = stickyBar.querySelector('.kashira-cart-stickybar__value');
        if (value && target) target.textContent = value.textContent;
      }).observe(totalsSource, { childList: true, subtree: true });
    }
  }

  /* The H1's "· N weaves" count lives outside `.js-contents`, so no re-render
     reaches it — derive it from the footer's item-count hook instead. */
  const countSource = document.querySelector('#main-cart-footer .js-contents');
  const countLabel = document.querySelector('[data-kashira-cart-count-label]');
  if (countSource && countLabel) {
    new MutationObserver(() => {
      const holder = countSource.querySelector('[data-kashira-item-count]');
      const count = holder ? parseInt(holder.dataset.kashiraItemCount, 10) : NaN;
      if (!Number.isFinite(count) || count < 1) return;
      const strings = window.kashiraCartStrings || {};
      const template = count === 1 ? strings.countOne : strings.countMany;
      if (template) countLabel.textContent = `· ${template.replace(/\d+/, count)}`;
    }).observe(countSource, { childList: true, subtree: true });
  }

  /* A removal earns its toast only once the server confirms it. cartUpdate
     fires even on the errors path (cartData.errors set) and a network failure
     publishes nothing — the expiry drops the pending entry then, and the
     motion asset un-collapses the row. */
  const pendingRemovals = new Map(); // lineKey -> pending record

  subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
    if (payload?.source !== 'cart-items' || !pendingRemovals.size) return;
    const cart = payload.cartData;
    if (!cart || !Array.isArray(cart.items)) return;
    pendingRemovals.forEach((pending, key) => {
      if (cart.errors) {
        window.clearTimeout(pending.expiry);
        pendingRemovals.delete(key);
        return;
      }
      if (cart.items.some((item) => item.key === key)) return; // a different mutation
      window.clearTimeout(pending.expiry);
      pendingRemovals.delete(key);
      showUndoToast(pending);
    });
  });

  const showUndoToast = (pending) => {
    if (!window.KashiraToast) return;
    const message = pending.move
      ? window.kashiraCartStrings?.movedToWishlist
      : window.kashiraCartStrings?.removed;
    window.KashiraToast.show((message || '') + (pending.title ? ` — ${pending.title}` : ''), {
      actionLabel: window.kashiraCartStrings?.undo,
      focusAction: pending.keyboard,
      onAction: () => performUndo(pending),
    });
  };

  const performUndo = async (pending) => {
    // Undoing a move restores both sides — the wishlist entry goes only
    // if the move created it (a heart set beforehand is kept).
    if (pending.move?.added) window.KashiraWishlistStore?.toggle(pending.move.handle);
    const drawer = pending.inDrawer ? document.querySelector('cart-drawer') : null;
    const sections = drawer
      ? drawer.getSectionsToRender().map((s) => s.id)
      : sectionIds().concat('cart-icon-bubble');
    try {
      const snapshot = (await pending.linePromise) || {
        id: Number(pending.variantId),
        quantity: pending.quantity,
        properties: null,
        selling_plan: null,
      };
      // The toast window is long enough for the shopper to have re-added the
      // line by hand — only top the cart back up to the removed quantity, or
      // undo after a manual re-add would leave more than they started with.
      const cart = await fetch(`${window.routes?.cart_url || '/cart'}.js`).then((r) => r.json());
      const propsOf = (value) => JSON.stringify(value || {});
      const existing = cart.items.find(
        (item) =>
          item.id === snapshot.id &&
          propsOf(item.properties) === propsOf(snapshot.properties) &&
          (item.selling_plan_allocation?.selling_plan?.id || null) === (snapshot.selling_plan || null)
      );
      const deficit = snapshot.quantity - (existing ? existing.quantity : 0);
      let payload;
      if (deficit > 0) {
        const body = { id: snapshot.id, quantity: deficit, sections, sections_url: window.location.pathname };
        if (snapshot.properties && Object.keys(snapshot.properties).length) body.properties = snapshot.properties;
        if (snapshot.selling_plan) body.selling_plan = snapshot.selling_plan;
        payload = await postCart(`${window.routes?.cart_add_url || '/cart/add'}.js`, body);
      } else {
        // Nothing to restore — refresh this surface so it shows the truth.
        payload = {
          sections: await fetch(`${window.location.pathname}?sections=${sections.join(',')}`).then((r) => r.json()),
        };
      }
      if (drawer && typeof drawer.renderContents === 'function') {
        // renderContents refreshes #CartDrawer and reopens the drawer, but
        // the empty flag sits on the <cart-drawer> root it never touches —
        // clear it, the cart cannot be empty after a restore.
        drawer.renderContents({ ...payload, id: snapshot.id });
        drawer.classList.remove('is-empty');
      } else {
        applyCart(payload);
      }
      announce(window.kashiraCartStrings?.lineUpdated);
      if (pending.keyboard) focusRestoredLine(pending, snapshot.id);
    } catch (error) {
      // Sold out or otherwise rejected — the cart is unchanged; say so
      // instead of reloading into silence.
      const message = window.kashiraCartStrings?.undoFailed || window.cartStrings?.error;
      window.KashiraToast?.show(message);
      announce(message);
    }
  };

  const focusRestoredLine = (pending, variantId) => {
    const scope = pending.inDrawer ? 'cart-drawer' : 'cart-items';
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => {
        document
          .querySelector(`${scope} input[data-quantity-variant-id="${variantId}"]`)
          ?.closest('.cart-item')
          ?.querySelector('.cart-item__name')
          ?.focus();
      })
    );
  };

  document.addEventListener('click', (e) => {
    const removeBtn = e.target.closest('cart-remove-button a, cart-remove-button button');
    if (!removeBtn || !e.target.closest('cart-drawer, cart-items')) return;

    const row = removeBtn.closest('.cart-item');
    const qtyInput = row?.querySelector('input[data-quantity-variant-id], input[name="updates[]"]');
    const variantId = qtyInput?.dataset.quantityVariantId || qtyInput?.dataset.variantId;
    const lineKey = qtyInput?.dataset.quantityLineKey;
    const quantity = parseInt(qtyInput?.value, 10) || 1;
    const title = row?.querySelector('.cart-item__name')?.textContent.trim().slice(0, 40);
    if (!variantId || !lineKey || !window.KashiraToast) return;

    const move = pendingMove;
    pendingRemovals.set(lineKey, {
      variantId,
      quantity,
      title,
      lineKey,
      move,
      inDrawer: Boolean(e.target.closest('cart-drawer')),
      keyboard: move ? move.keyboard : e.detail === 0,
      // Snapshot the full line before the (deferred) removal lands, so UNDO
      // can restore properties and selling plan, not just {id, quantity}.
      linePromise: fetch(`${window.routes?.cart_url || '/cart'}.js`)
        .then((r) => r.json())
        .then((cartNow) => {
          const item = cartNow.items.find((i) => i.key === lineKey);
          return item
            ? {
                id: item.id,
                quantity: item.quantity,
                properties: item.properties || null,
                selling_plan: item.selling_plan_allocation?.selling_plan?.id || null,
              }
            : null;
        })
        .catch(() => null),
      expiry: window.setTimeout(() => pendingRemovals.delete(lineKey), 10000),
    });
  });
})();
